import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Save, StickyNote, Search, X } from "lucide-react";
import { toast } from "sonner";
import MiniCalendar from "@/components/MiniCalendar";



const NotesPage = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [datesWithEntries, setDatesWithEntries] = useState([]);
  const [newNote, setNewNote] = useState({ title: "", content: "" });
  const [editingNote, setEditingNote] = useState(null);

  useEffect(() => {
    fetchNotes();
    fetchDatesWithEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDatesWithEntries = async () => {
    try {
      const response = await api.get('/notes/dates/with-entries');
      setDatesWithEntries(response.data || []);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error fetching dates:", error);
    }
  };

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/notes`);
      setNotes(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento delle note");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNote = async (e) => {
    e.preventDefault();
    if (!newNote.content.trim()) {
      toast.error("Scrivi qualcosa nella nota");
      return;
    }
    
    try {
      if (editingNote) {
        await api.put(`/notes/${editingNote.note_id}`, {
          title: newNote.title,
          content: newNote.content
        });
        toast.success("Nota aggiornata");
      } else {
        await api.post(`/notes`, {
          title: newNote.title,
          content: newNote.content
        });
        toast.success("Nota salvata");
      }
      setNewNote({ title: "", content: "" });
      setEditingNote(null);
      setDialogOpen(false);
      fetchNotes();
      fetchDatesWithEntries();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleEditNote = (note) => {
    setEditingNote(note);
    setNewNote({ title: note.title || "", content: note.content });
    setDialogOpen(true);
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Eliminare questa nota?")) return;
    
    try {
      await api.delete(`/notes/${noteId}`);
      toast.success("Nota eliminata");
      fetchNotes();
      fetchDatesWithEntries();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleDateSelect = (date) => {
    if (selectedDate === date) {
      setSelectedDate(null); // Deselect to show all
    } else {
      setSelectedDate(date);
    }
  };

  const clearDateFilter = () => {
    setSelectedDate(null);
  };

  const handleNewNote = () => {
    setEditingNote(null);
    setNewNote({ title: "", content: "" });
    setDialogOpen(true);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter notes by search term and date
  const filteredNotes = notes.filter(note => {
    const matchesSearch = (note.title?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
      note.content.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!selectedDate) return matchesSearch;
    
    // Filter by date
    const noteDate = note.created_at?.split("T")[0];
    return matchesSearch && noteDate === selectedDate;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-foreground border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in pb-8" data-testid="notes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div>
          <h1 className="page-title">Note Personali</h1>
          <p className="page-subtitle">I tuoi appunti e pensieri</p>
        </div>
        
        <Button onClick={handleNewNote} className="rounded-sm" data-testid="new-note-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuova Nota
        </Button>
      </div>

      {/* Layout: Calendar + Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        
        {/* Mini Calendar Sidebar */}
        <div className="lg:col-span-1">
          <MiniCalendar
            datesWithEntries={datesWithEntries}
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
          />
          <p className="text-xs text-gray-500 text-center mt-3">
            {datesWithEntries.length} giorni con note
          </p>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3 space-y-4">
          {/* Search + Date Filter */}
          <div className="flex flex-col sm:flex-row gap-3">
            {notes.length > 0 && (
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Cerca nelle note..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 rounded-xl"
                  data-testid="search-notes"
                />
              </div>
            )}
            
            {selectedDate && (
              <div className="flex items-center gap-2 bg-indigo-50 text-indigo-700 px-3 py-2 rounded-xl text-sm">
                <span>
                  Filtro: {new Date(selectedDate).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
                <button onClick={clearDateFilter} className="hover:bg-indigo-100 rounded p-0.5">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {/* Notes Grid */}
          {filteredNotes.length === 0 ? (
            <Card className="border border-dashed border-border">
              <CardContent className="py-12 text-center">
                <StickyNote className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  {searchTerm || selectedDate ? "Nessuna nota trovata" : "Nessuna nota ancora. Crea la prima!"}
                </p>
                {!searchTerm && !selectedDate && (
                  <Button onClick={handleNewNote} variant="outline" className="mt-4 rounded-sm">
                    <Plus className="w-4 h-4 mr-1" /> Crea Nota
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredNotes.map((note) => (
                <Card 
                  key={note.note_id} 
                  className="border border-border shadow-sm rounded-xl hover:shadow-md transition-shadow cursor-pointer group"
                  onClick={() => handleEditNote(note)}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <CardTitle className="font-heading text-base line-clamp-1">
                        {note.title || "Senza titolo"}
                      </CardTitle>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteNote(note.note_id);
                        }}
                        className="opacity-0 group-hover:opacity-100 text-destructive p-1 hover:bg-destructive/10 rounded transition-opacity"
                        data-testid={`delete-note-${note.note_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(note.updated_at || note.created_at)}
                    </p>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground line-clamp-4 whitespace-pre-wrap">
                      {note.content}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Note Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingNote ? "Modifica Nota" : "Nuova Nota"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveNote} className="space-y-4">
            <Input
              placeholder="Titolo (opzionale)"
              value={newNote.title}
              onChange={(e) => setNewNote({ ...newNote, title: e.target.value })}
              className="rounded-sm"
              data-testid="note-title"
            />
            <Textarea
              placeholder="Scrivi qui la tua nota..."
              value={newNote.content}
              onChange={(e) => setNewNote({ ...newNote, content: e.target.value })}
              className="rounded-sm min-h-[200px]"
              data-testid="note-content"
              autoFocus
            />
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                Annulla
              </Button>
              <Button type="submit" className="rounded-sm" data-testid="save-note-btn">
                <Save className="w-4 h-4 mr-1" /> Salva
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotesPage;
